import cx from 'clsx';
import Tree from 'rc-tree';
import type { Key } from 'rc-tree/lib/interface';
import React, { useEffect, useState } from 'react';

import { ReactComponent as ThinArrowIcon } from '@assets/svg/thin-arrow.svg';

import Button from '@components/uikit/Button/Button';

import useTranslation from '@hooks/useTranslation';

import {
  selectSensorLocationId,
  selectSensorSearch,
  useSensorStoreActions,
} from '@pages/Sensors/store/useSensorStore';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import style from './style.module.scss';

interface IProps {
  sensorLocations?: { [key: string]: boolean };
  sensorFacilities?: Array<number>;
}

const SensorsFacilityFilter: React.FC<IProps> = ({ sensorLocations = {}, sensorFacilities = [] }) => {
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const { data: assetTreeData } = useAssetTreeQuery();

  const { setSensorLocationId } = useSensorStoreActions();

  const locationSelectValue = selectAppLocationSelectValue();
  const locationId = selectSensorLocationId();
  const searchValue = selectSensorSearch();

  const facilityStructureData = assetTreeData?.facilities || [];
  const allLocationIds = assetTreeData?.locationsIds || [];
  const isFullFilter = locationId && searchValue;

  useEffect(() => {
    const isNotAllLocation =
      facilityStructureData.length > 1 && locationSelectValue.level_name !== 'allFacilities';

    if ((!locationId || isNotAllLocation) && !searchValue) {
      setSensorLocationId(isNotAllLocation ? locationSelectValue.filterIds[0] : facilityStructureData[0]?.id);
    }
  }, [searchValue]);

  const filterTreeNode = (node: any) => {
    if (!searchValue) {
      return false;
    }

    return sensorLocations[node.id];
  };

  useEffect(() => {
    if (sensorFacilities.length) {
      setExpandedKeys(sensorFacilities);
    }
  }, [sensorFacilities]);

  return (
    <div className={style.filterBlock}>
      <div className={style.filterHead}>
        <div className={style.filterHeadTitle}>{t('Facility')}</div>
        <Button
          type="secondary"
          text={expandedKeys.length ? t('Collapse all') : t('Expand all')}
          onClick={() => setExpandedKeys(expandedKeys.length ? [] : allLocationIds)}
        />
      </div>
      <div className={cx(style.filterBody, { [style.filterBodyShort]: isFullFilter })}>
        <Tree
          className={style.facilitiesTree}
          treeData={facilityStructureData}
          selectable
          fieldNames={{
            children: 'children',
            title: 'name',
            key: 'id',
          }}
          showIcon={false}
          showLine={false}
          switcherIcon={<ThinArrowIcon />}
          expandedKeys={expandedKeys}
          filterTreeNode={filterTreeNode}
          onExpand={setExpandedKeys}
          selectedKeys={[locationId]}
          onSelect={(selectedKeys) => setSensorLocationId(selectedKeys[0])}
        />
      </div>
      {isFullFilter && (
        <div className={style.filterFooter}>
          <Button
            type="secondary"
            text={t('View all results')}
            onClick={() => setSensorLocationId(undefined)}
          />
        </div>
      )}
    </div>
  );
};

export default SensorsFacilityFilter;
